import {useCallback, useEffect, useRef, useState} from 'react';
import {setETicketTicketScanned} from '../../../../../redux/eTicket/eTicketActions';
import {clearQrScanBurst, scheduleQrScanSequence} from './qrScanSequence';
import useQrScanPoll from './useQrScanPoll';

interface UseQrZoomScanOptions {
    token?: string;
    qid: number;
    initialScanned?: boolean;
    initialCanceled?: boolean;
    initialScanTs?: number;
    reduceMotion: boolean | null;
    resolveScanTarget: () => HTMLElement | null;
    pollEnabled?: boolean;
}

interface UseQrZoomScanResult {
    scanned: boolean;
    scanTs?: number;
    isScanning: boolean;
    isPop: boolean;
}

function useQrZoomScan({
    token,
    qid,
    initialScanned = false,
    initialCanceled = false,
    initialScanTs,
    reduceMotion,
    resolveScanTarget,
    pollEnabled = false,
}: UseQrZoomScanOptions): UseQrZoomScanResult {
    const pendingScanTsRef = useRef(initialScanTs);
    const scanTargetRef = useRef<HTMLElement | null>(null);
    const [scanned, setScanned] = useState(!!initialScanned);
    const [isScanning, setIsScanning] = useState(false);
    const isPop = scanned && !initialScanned;
    const scanTs = scanned ? pendingScanTsRef.current : undefined;

    const handleScannedDetected = useCallback((ts?: number) => {
        pendingScanTsRef.current = ts;
        setETicketTicketScanned(qid, ts);

        if(reduceMotion) {
            setScanned(true);
            return;
        }

        const target = resolveScanTarget();
        if(!target) {
            setScanned(true);
            return;
        }

        scanTargetRef.current = target;
        setIsScanning(true);
    }, [qid, reduceMotion, resolveScanTarget]);

    useQrScanPoll({
        token,
        qid,
        enabled: pollEnabled && !initialScanned && !initialCanceled,
        onScanned: handleScannedDetected,
    });

    useEffect(() => () => clearQrScanBurst(), []);

    useEffect(() => {
        const el = scanTargetRef.current;
        if(!isScanning || !el) {
            return;
        }

        return scheduleQrScanSequence(el, {
            onStatus: () => setScanned(true),
            onEnd: () => setIsScanning(false),
        });
    }, [isScanning]);

    return {scanned, scanTs, isScanning, isPop};
}

export default useQrZoomScan;
